// The Disposition & handoff tab's types, derived from the generated client (api/tools.ts, regenerated from the tools'
// pydantic models): the tab names what it reads and writes, and a server change reaches it through `make gen-client`.
// Only the closed vocabularies the server's models carry as plain strings are narrowed here.

import type {
  ApplicationOut, ArchiveOut, CanOut, ClockOut, CloseoutOpenOutput, DispositionOpenWorklistOutput, DispositionRecordInput,
  EvidenceOut, FinalTermsOut, HandoffRecordItemInput, HandoffValidateInput, HandoffValidateOutput, HrisConfirmInput,
  HrisConfirmOutput, HrisCorrectInput, HrisOut, ItemStateOut, OpeningDatesOut, ReasonOut, Row, StatusWordOut,
  TalentDesignateInput, TalentDesignateOutput,
} from "@/api/tools";

/** One handoff checklist item as the reader sees it (S06 `ItemState`). */
export type ItemState = Omit<ItemStateOut, "state" | "source"> & {
  /** done · not_required · pending; null when nothing is recorded or derived yet. */
  state: HandoffRecordItemInput["state"] | null;
  /** recorded · derived · adapter */
  source: "recorded" | "derived" | "adapter" | null;
};

/** The live HRIS confirmation of an application (S08); `employee_id` is null where the reader's projection trims it. */
export type HrisFacts = HrisOut;
/** What an adapter reported (S08 `hris_evidence`): never a confirmation. */
export type HrisEvidenceFacts = EvidenceOut;
/** The accepted package's final terms, projected by field policy: a key the seat may not read is absent. */
export type FinalTerms = FinalTermsOut;
/** The writes this seat may make on this application, decided on the server (D13). The Admin holds them as its own
 *  role (DC-228); no write goes by override, so the client never reads an `override` flag. */
export type CloseoutCan = CanOut;

export type CloseoutApplication = Omit<ApplicationOut, "checklist"> & { checklist: ItemState[] };
/** Per opening, each date its own event (S10). */
export type OpeningDates = OpeningDatesOut;
/** One of the three closeout clocks (S10): Time to Fill, Acceptance to HRIS, Acceptance to Start. */
export type ClockFigure = ClockOut;
export type StatusWord = StatusWordOut;
export type ArchiveRef = ArchiveOut;

/** `closeout.open {requisition_id}` (S06, with S08's HRIS facts and S10's dates and clocks). */
export type CloseoutOut = Omit<CloseoutOpenOutput, "applications"> & { applications: CloseoutApplication[] };

/** A reason with the configuration version it was chosen from (S09 via `offer_facts`). */
export type ReasonFact = ReasonOut;
/** One `disposition.open_worklist` row: V1's fields plus S09's reasons, eligibility and membership. */
export type WorklistRow = Row;

/** `disposition.open_worklist`: the server sends its codes and keep-warm controls as open records; the tab reads them
 *  by these keys. */
export type WorklistOut = Omit<DispositionOpenWorklistOutput, "projection" | "codes" | "keep_warm"> & {
  projection: "full" | "count";
  codes: { slug: string; label: string }[];
  keep_warm: { application_id: string; token: string; keep_warm: boolean }[];
};

/** B3's five bases (models/talent.REASONS_ADDED), the generated input's own union. */
export type ReasonAdded = TalentDesignateInput["reason_added"];
export const REASONS_ADDED: readonly ReasonAdded[] = ["finalist", "alternate", "silver_medalist", "timing_decline", "timing_withdrawal"];

// ---- write inputs and outputs ----------------------------------------------------------------------------------------
export type RecordItemInput = HandoffRecordItemInput;
export type ValidateInput = HandoffValidateInput;
export type ValidateOut = HandoffValidateOutput;
export type ConfirmInput = HrisConfirmInput;
export type ConfirmOut = HrisConfirmOutput;
export type CorrectInput = HrisCorrectInput;
export type DesignateInput = TalentDesignateInput;
export type DesignateOut = TalentDesignateOutput;
export type DispositionInput = DispositionRecordInput;

/** Every write the tab makes, by tool, so the page runs them through one door (callTool + Idempotency-Key). */
export type ClosureWrite =
  | { tool: "handoff.record_item"; input: RecordItemInput; reason?: string | undefined }
  | { tool: "handoff.validate"; input: ValidateInput; reason?: string | undefined }
  | { tool: "hris.confirm"; input: ConfirmInput; reason?: string | undefined }
  | { tool: "hris.correct"; input: CorrectInput; reason?: string | undefined }
  | { tool: "talent.designate"; input: DesignateInput; reason?: string | undefined }
  | { tool: "disposition.record"; input: DispositionInput; reason?: string | undefined };

/** The host's write door: resolves when the write committed, rejects with the server's problem. */
export type RunWrite = (write: ClosureWrite) => Promise<void>;
